/**
 * OutcomeParadoxSection.jsx — GRANULLO_OUTCOME_PARADOX_CINEMATIC_MASTERPIECE_V2
 *
 * 4-ACT CINEMATIC CONFRONTATION:
 * Header: "We are changing the world." / "How many times have you heard this?"
 * ACT 1: THE PROMISE — animated allocation counters
 * ACT 2: THE DISSOLVE — money journey collapse flow (left→right degradation)
 * ACT 3: THE CONFRONTATION — forensic doodle evidence wall
 * ACT 4: THE INEVITABILITY — Granullo enters
 */
import React, { useRef, useState, useEffect } from 'react';
import { motion, useInView } from 'framer-motion';
import HandDrawnFlowBar from './HandDrawnFlowBar';

const RED = '#CD2200';
const AMBER = '#D97706';
const GREEN = '#22C55E';

const stagger = { hidden: {}, show: { transition: { staggerChildren: 0.08 } } };
const fadeUp = { hidden: { opacity: 0, y: 16 }, show: { opacity: 1, y: 0, transition: { duration: 0.5 } } };

/* ═══ DARK STAGE ═══ */
const DarkStage = ({ children, className = '', testId }) => (
  <div className={`relative ${className}`} style={{ background: 'var(--shell-bg, #050505)', minHeight: '100vh' }} data-testid={testId}>
    <div className="absolute inset-0 pointer-events-none" style={{
      backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='n'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23n)' opacity='0.04'/%3E%3C/svg%3E")`,
      opacity: 0.12, mixBlendMode: 'overlay',
    }} />
    <div className="relative z-[1]">{children}</div>
  </div>
);

/* ═══ ANIMATED COUNTER ═══ */
const AnimatedCounter = ({ target, prefix = '$' }) => {
  const ref = useRef(null);
  const inView = useInView(ref, { once: true, margin: '-50px' });
  const [count, setCount] = useState(0);

  useEffect(() => {
    if (!inView) return;
    let start = 0;
    const step = target / 125;
    const timer = setInterval(() => {
      start += step;
      if (start >= target) { setCount(target); clearInterval(timer); }
      else setCount(Math.floor(start * 10) / 10);
    }, 16);
    return () => clearInterval(timer);
  }, [inView, target]);

  const display = count >= 1000 ? `${(count / 1000).toFixed(1)}T` : `${count.toFixed(0)}B`;
  return <span ref={ref} className="tabular-nums">{prefix}{display}</span>;
};

/* ═══ ALLOCATION DATA ═══ */
const ALLOCATIONS = [
  { label: 'Climate Funding', target: 3200, color: '#22C55E' },
  { label: 'Global Aid', target: 980, color: '#6B6B6B' },
  { label: 'Welfare Programs', target: 1400, color: '#A855F7' },
  { label: 'Education Budgets', target: 620, color: '#F59E0B' },
  { label: 'Health Spending', target: 850, color: '#EC4899' },
  { label: 'Disaster Relief', target: 340, color: '#06B6D4' },
];

/* ═══ MONEY JOURNEY DISSOLUTION HOPS ═══ */
const DISSOLUTION_HOPS = [
  { label: 'Ministry allocation', lost: '8%', annotation: 'admin overhead' },
  { label: 'State agency', lost: '11%', annotation: 'bureaucratic lag' },
  { label: 'District office', lost: '9%', annotation: 'duplicate reporting' },
  { label: 'Intermediary admin', lost: '14%', annotation: 'non-verifiable spend' },
  { label: 'Verification hold', lost: '12%', annotation: 'settlement mismatch' },
  { label: 'Beneficiary layer', lost: '18%', annotation: 'ghost recipients' },
];

/* ═══ CONFRONTATION FORENSIC EVIDENCE ═══ */
const FORENSIC_EVIDENCE = [
  {
    stat: '828M', reality: 'still hungry?', despite: 'despite $300B+ annual food security spend',
    annotation: 'after trillions in food programs', circle: true,
  },
  {
    stat: '1.5°C', reality: 'after trillions?', despite: 'despite $3.2T climate commitments',
    annotation: 'warming threshold already breached', underline: true,
  },
  {
    stat: '250M', reality: 'still can\'t read', despite: 'despite $620B annual education budgets',
    annotation: 'books never arrived', circle: true,
  },
  {
    stat: '40%', reality: 'never reached farmers', despite: 'despite decades of reform promises',
    annotation: 'subsidy leakage unchanged', underline: true,
  },
];

const FIELD_NOTES = [
  {
    title: 'THE FARMER',
    scene: 'Subsidy announced on national television. Six months later, he is still waiting.',
    detail: 'The money exists. The approval exists. The farmer exists. The arrival doesn\'t.',
    stamp: 'still waiting',
  },
  {
    title: 'THE CLASSROOM',
    scene: 'Education budget increased by 12%. The school still has no books.',
    detail: 'Budget lines don\'t teach children. Verified deliveries do.',
    stamp: 'books never arrived',
  },
];

/* ═══ MAIN SECTION ═══ */
export default function OutcomeParadoxSection() {
  return (
    <section data-testid="outcome-paradox-section">
      {/* ═══ CHAPTER HEADER ═══ */}
      <DarkStage testId="paradox-header-stage" className="flex items-center justify-center py-24 sm:py-32 px-6">
        <div className="text-center max-w-3xl">
          <motion.h2
            className="text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-[-0.03em] leading-[1.1] mb-3"
            style={{ color: 'var(--shell-text)', fontFamily: '"Inter Tight", sans-serif' }}
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            data-testid="paradox-main-header"
          >
            "We are changing the world."
          </motion.h2>
          <motion.p
            className="-rotate-2"
            style={{ fontFamily: '"Kalam", cursive', fontSize: 'clamp(20px, 4vw, 32px)', color: RED }}
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.3, duration: 0.5 }}
            data-testid="paradox-interruption"
          >
            How many times have you heard this?
          </motion.p>
          <motion.p
            className="text-sm sm:text-base font-mono uppercase tracking-[0.15em] mt-6"
            style={{ color: 'var(--shell-text-muted)' }}
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ delay: 0.6 }}
            data-testid="paradox-subline"
          >
            Trillions moved. Nothing changed.
          </motion.p>
        </div>
      </DarkStage>

      {/* ═══ ACT 1 — THE PROMISE ═══ */}
      <DarkStage testId="act-1-container" className="flex flex-col items-center justify-center py-20 sm:py-28 px-6">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] mb-8" style={{ color: 'var(--shell-text-muted)' }}>
          Act I — The Promise
        </p>
        <motion.div
          className="grid grid-cols-2 sm:grid-cols-3 gap-5 sm:gap-8 max-w-[720px] w-full"
          variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
        >
          {ALLOCATIONS.map((a) => (
            <motion.div key={a.label} className="text-center" variants={fadeUp}>
              <div className="text-3xl sm:text-4xl lg:text-5xl font-bold tracking-tight mb-1" style={{ color: a.color, fontFamily: '"Inter Tight", sans-serif' }}>
                <AnimatedCounter target={a.target} />
              </div>
              <span className="text-[11px] block" style={{ color: 'var(--shell-text-muted)' }}>{a.label}</span>
            </motion.div>
          ))}
        </motion.div>
        <motion.p
          className="text-[13px] mt-10 max-w-md text-center"
          style={{ color: 'var(--shell-text-muted)' }}
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }}
        >
          Trillions pledged. Headlines made.
          <br />The commitment was celebrated. The arrival never was.
        </motion.p>
      </DarkStage>

      {/* ═══ ACT 2 — THE DISSOLVE (MONEY JOURNEY COLLAPSE) ═══ */}
      <DarkStage testId="act-2-container" className="flex flex-col items-center justify-center py-20 sm:py-28 px-6">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] mb-2" style={{ color: 'var(--shell-text-muted)' }}>
          Act II — The Dissolve
        </p>
        {/* Plain-language one-liner so the chart reads instantly */}
        <motion.p
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true, margin: '-40px' }}
          className="text-[12px] sm:text-[13px] mb-8 text-center max-w-[520px]"
          style={{ color: 'var(--shell-text-muted)' }}
          data-testid="dissolve-subtitle"
        >
          <span style={{ color: 'var(--shell-text)', fontWeight: 600 }}>$420M promised</span>
          {' → '}
          <span style={{ color: RED, fontWeight: 600 }}>$118M arrives.</span>
          {' '}Every layer below takes a cut.
        </motion.p>

        {/* Money journey collapse flow */}
        <div className="max-w-[780px] w-full">
          {/* Source node */}
          <motion.div
            className="flex items-center gap-4 mb-6"
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6 }}
          >
            <div className="shrink-0 w-24 sm:w-32 h-16 sm:h-20 rounded-lg flex flex-col items-center justify-center" style={{
              background: 'var(--shell-surface)', border: '1px solid var(--shell-border)',
              boxShadow: '0 0 20px var(--shell-border)',
            }}>
              <span className="text-xl sm:text-2xl font-bold" style={{ color: 'var(--shell-text)', fontFamily: '"Inter Tight", sans-serif' }}>$420M</span>
              <span className="text-[8px] font-mono uppercase" style={{ color: 'var(--shell-text-muted)' }}>committed</span>
            </div>
            <div className="flex-1 h-px relative" style={{ background: 'var(--shell-border)' }}>
              <motion.div className="absolute inset-0 h-px" style={{ background: `linear-gradient(90deg, var(--shell-text-muted), ${RED}40)`, transformOrigin: 'left' }}
                initial={{ scaleX: 0 }} whileInView={{ scaleX: 1 }} viewport={{ once: true }}
                transition={{ delay: 0.3, duration: 1.2 }}
              />
            </div>
          </motion.div>

          {/* Dissolution hops */}
          <motion.div
            className="space-y-2"
            variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
          >
            {DISSOLUTION_HOPS.map((hop, i) => {
              const remaining = 100 - DISSOLUTION_HOPS.slice(0, i + 1).reduce((s, h) => s + parseInt(h.lost), 0);
              return (
                <motion.div key={hop.label} className="flex items-center gap-3" variants={fadeUp}>
                  {/* Hop label */}
                  <span className="text-[10px] font-mono w-28 sm:w-36 text-right shrink-0" style={{ color: 'var(--shell-text-muted)' }}>
                    {hop.label}
                  </span>
                  {/* Flow bar with leakage — hand-drawn signature */}
                  <div className="flex-1 h-7 relative flex items-center">
                    <HandDrawnFlowBar
                      remainingPct={remaining}
                      lostPct={parseInt(hop.lost)}
                      index={i}
                      seed={101 + i * 37}
                      testId={`dissolution-hop-bar-${i}`}
                    />
                    <span className="absolute right-2 top-1/2 -translate-y-1/2 text-[9px] font-mono font-medium z-[1]" style={{ color: `${RED}CC` }}>
                      -{hop.lost}
                    </span>
                  </div>
                  {/* Handwritten annotation */}
                  <span className="text-[9px] w-24 sm:w-32 hidden sm:block -rotate-1" style={{ fontFamily: '"Caveat", cursive', color: `${RED}80` }}>
                    {hop.annotation}
                  </span>
                </motion.div>
              );
            })}
          </motion.div>

          {/* Arrival node */}
          <motion.div
            className="flex items-center gap-4 mt-6"
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: 0.8, duration: 0.6 }}
          >
            <div className="flex-1 h-px" style={{ background: `${RED}20` }} />
            <div className="shrink-0 w-24 sm:w-32 h-16 sm:h-20 rounded-lg flex flex-col items-center justify-center" style={{
              background: `${RED}08`, border: `1px solid ${RED}20`,
            }}>
              <span className="text-xl sm:text-2xl font-bold" style={{ color: RED, fontFamily: '"Inter Tight", sans-serif' }}>$118M</span>
              <span className="text-[8px] font-mono uppercase" style={{ color: `${RED}80` }}>verified arrival</span>
            </div>
          </motion.div>

          {/* Human consequence */}
          <motion.p
            className="text-[11px] mt-4 text-right italic"
            style={{ color: 'var(--shell-text-muted)' }}
            initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 1.2 }}
          >
            crop season already missed. school term already started. medicine stock still empty.
          </motion.p>
        </div>

        {/* Handwritten verdict */}
        <motion.p
          className="mt-10 text-center -rotate-1"
          style={{ fontFamily: '"Kalam", cursive', fontSize: 'clamp(22px, 4.5vw, 36px)', color: RED }}
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.6 }}
          data-testid="dissolve-handwritten"
        >
          "We funded the narrative. Not the outcome."
        </motion.p>
      </DarkStage>

      {/* ═══ ACT 3 — THE CONFRONTATION (FORENSIC EVIDENCE WALL) ═══ */}
      <DarkStage testId="act-3-container" className="flex flex-col items-center justify-center py-20 sm:py-28 px-6">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] mb-8" style={{ color: 'var(--shell-text-muted)' }}>
          Act III — The Confrontation
        </p>

        {/* Forensic evidence tiles */}
        <motion.div
          className="grid grid-cols-1 sm:grid-cols-2 gap-5 max-w-[700px] w-full"
          variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
        >
          {FORENSIC_EVIDENCE.map((ev) => (
            <motion.div
              key={ev.stat}
              className="p-5 rounded-lg relative overflow-hidden"
              style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)' }}
              variants={fadeUp}
            >
              {/* Forensic doodle overlays */}
              {ev.circle && (
                <svg className="absolute top-2 right-2 w-16 h-16 opacity-30 pointer-events-none" viewBox="0 0 60 60">
                  <ellipse cx="30" cy="30" rx="26" ry="24" fill="none" stroke={RED} strokeWidth="1.5"
                    strokeDasharray="4 3" transform="rotate(-5 30 30)" opacity="0.5" />
                </svg>
              )}
              {ev.underline && (
                <svg className="absolute bottom-8 left-4 w-32 h-4 opacity-40 pointer-events-none" viewBox="0 0 120 12">
                  <path d="M2 8 Q30 2 60 9 Q90 4 118 7" fill="none" stroke={RED} strokeWidth="1.5" strokeLinecap="round" />
                </svg>
              )}

              <div className="relative z-[1]">
                <div className="text-3xl sm:text-4xl font-bold mb-1" style={{ color: RED, fontFamily: '"Inter Tight", sans-serif' }}>
                  {ev.stat}
                </div>
                {/* Handwritten reality question */}
                <p className="-rotate-1 mb-2" style={{ fontFamily: '"Kalam", cursive', fontSize: 16, color: RED }}>
                  {ev.reality}
                </p>
                <p className="text-[11px]" style={{ color: 'var(--shell-text-muted)' }}>
                  {ev.despite}
                </p>
                {/* Margin note annotation */}
                <p className="text-[9px] mt-2 -rotate-1" style={{ fontFamily: '"Caveat", cursive', color: `${AMBER}80` }}>
                  {ev.annotation}
                </p>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Field notes (forensic evidence cards) */}
        <motion.div
          className="mt-8 max-w-[700px] w-full space-y-3"
          variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
        >
          {FIELD_NOTES.map((note) => (
            <motion.div
              key={note.title}
              className="flex items-start gap-4 p-4 rounded-lg relative overflow-hidden"
              style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)' }}
              variants={fadeUp}
            >
              {/* Handwritten label */}
              <div className="shrink-0 w-20 pt-0.5">
                <span className="-rotate-2 inline-block text-[11px] font-medium" style={{ fontFamily: '"Caveat", cursive', color: RED }}>
                  {note.title}
                </span>
              </div>
              <div className="flex-1">
                <p className="text-[12px] leading-relaxed" style={{ color: 'var(--shell-text-secondary)' }}>{note.scene}</p>
                <p className="text-[10px] mt-1.5 italic" style={{ color: 'var(--shell-text-muted)' }}>{note.detail}</p>
              </div>
              {/* Handwritten stamp */}
              <div className="shrink-0 self-end">
                <span className="-rotate-3 inline-block px-2 py-0.5 rounded text-[9px]" style={{
                  fontFamily: '"Caveat", cursive', color: RED,
                  border: `1px solid ${RED}40`, background: `${RED}08`,
                }}>
                  {note.stamp}
                </span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* Final verdict */}
        <motion.div
          className="mt-10 text-center relative"
          initial={{ opacity: 0 }} whileInView={{ opacity: 1 }} viewport={{ once: true }} transition={{ delay: 0.5 }}
        >
          {/* Imperfect circle around verdict */}
          <svg className="absolute -inset-x-4 -inset-y-2 w-[calc(100%+32px)] h-[calc(100%+16px)] opacity-30 pointer-events-none" viewBox="0 0 300 50" preserveAspectRatio="none">
            <ellipse cx="150" cy="25" rx="145" ry="22" fill="none" stroke={RED} strokeWidth="1"
              strokeDasharray="6 4" transform="rotate(-1 150 25)" />
          </svg>
          <p className="relative z-[1]" style={{ fontFamily: '"Kalam", cursive', fontSize: 'clamp(20px, 4vw, 30px)', color: 'var(--shell-text-secondary)' }}>
            Money moved. Reality didn't.
          </p>
        </motion.div>
      </DarkStage>

      {/* ═══ ACT 4 — THE INEVITABILITY ═══ */}
      <DarkStage testId="act-4-container" className="flex flex-col items-center justify-center py-20 sm:py-32 px-6">
        <p className="text-[10px] font-mono uppercase tracking-[0.2em] mb-8" style={{ color: 'var(--shell-text-muted)' }}>
          Act IV — The Inevitability
        </p>

        {/* Remaining human evidence */}
        <motion.div
          className="max-w-[600px] w-full space-y-3 mb-10"
          variants={stagger} initial="hidden" whileInView="show" viewport={{ once: true }}
        >
          {[
            { title: 'THE FLOOD SURVIVOR', scene: '$4.2B pledged in disaster relief. Families rebuild with their own savings.', detail: 'Global donations became global reports. The water damage remained local.', stamp: 'still rebuilding' },
            { title: 'THE PATIENT', scene: 'Health coverage approved. Medicine available 200km away.', detail: 'Insurance without verified pharmacy reach is just paperwork with a logo.', stamp: 'medicine delayed' },
          ].map((note) => (
            <motion.div
              key={note.title}
              className="flex items-center gap-4 p-4 rounded-lg"
              style={{ background: 'var(--shell-surface)', border: '1px solid var(--shell-border)' }}
              variants={fadeUp}
            >
              <div className="shrink-0 w-20">
                <span className="-rotate-2 inline-block text-[11px] font-medium" style={{ fontFamily: '"Caveat", cursive', color: RED }}>{note.title}</span>
              </div>
              <div className="flex-1">
                <p className="text-[12px] leading-relaxed" style={{ color: 'var(--shell-text-secondary)' }}>{note.scene}</p>
                <p className="text-[10px] mt-1.5 italic" style={{ color: 'var(--shell-text-muted)' }}>{note.detail}</p>
              </div>
              <div className="shrink-0 w-24 flex justify-end">
                <span className="-rotate-3 inline-block px-2 py-0.5 rounded text-[9px] whitespace-nowrap" style={{
                  fontFamily: '"Caveat", cursive', color: RED, border: `1px solid ${RED}40`, background: `${RED}08`,
                }}>{note.stamp}</span>
              </div>
            </motion.div>
          ))}
        </motion.div>

        {/* The inevitability statement */}
        <motion.div
          className="text-center max-w-[600px] w-full"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <p className="text-[14px] sm:text-[16px] leading-relaxed mb-5" style={{ color: 'var(--shell-text-secondary)' }}>
            Money never lacked.
            <br />Proof did.
          </p>
          <p className="text-[18px] sm:text-[22px] font-semibold tracking-tight leading-snug" style={{ color: 'var(--shell-text)', fontFamily: '"Inter Tight", sans-serif' }}>
            Money does not move
            <br /><span style={{ color: RED }}>until arrival is structurally proven.</span>
          </p>
        </motion.div>

        {/* Final handwritten punch */}
        <motion.p
          className="mt-10 -rotate-1 text-center max-w-[600px]"
          style={{ fontFamily: '"Kalam", cursive', fontSize: 'clamp(18px, 2.4vw, 26px)', color: RED, lineHeight: 1.3 }}
          initial={{ opacity: 0, scale: 0.95 }}
          whileInView={{ opacity: 1, scale: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
          data-testid="inevitability-handwritten"
        >
          "The problem was never money. It was arrival."
        </motion.p>
      </DarkStage>
    </section>
  );
}
